import type { AdListItem } from '../types/ad';
import type { Listing, BadgeType, Tab } from '../types';

// ─────────────────────────────────────────────────────────────
// Adapter: `AdListItem` (live API) → `Listing` (legacy card shape)
// ─────────────────────────────────────────────────────────────
//
// The UI layer (ListingCard, savedItems, etc.) was built around a numeric
// `id` and a specific shape. Rather than rewriting every consumer, this
// adapter maps the live API response into that shape while preserving the
// original UUID on `listing.uuid` so routing still targets the real backend
// record.

const FALLBACK_IMAGE =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 900 600"><rect width="100%" height="100%" fill="%23f3f4f6"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-family="sans-serif" font-size="32">No Image</text></svg>';

/** Map the root segment of `category_path` onto our existing Tab vocabulary. */
const TAG_FROM_PATH: Record<string, string> = {
  'cars & vehicles': 'Cars',
  cars: 'Cars',
  property: 'Property',
  mobiles: 'Mobiles',
  jobs: 'Jobs',
  bikes: 'Bikes',
  electronics: 'Electronics',
  fashion: 'Fashion',
  furniture: 'Furniture',
};

/** Map a Tab value to a backend category slug for the `?category=` filter. */
export function tabToCategorySlug(tab: Tab): string | undefined {
  switch (tab) {
    case 'Cars & Vehicles':
      return 'cars-vehicles';
    case 'Property':
      return 'property';
    case 'Mobiles':
      return 'mobile-phones';
    case 'Jobs':
      return 'jobs';
    case 'Bikes':
      return 'motorbikes-scooters';
    case 'All':
    default:
      return undefined;
  }
}

/** Stable positive numeric hash of a string (djb2-ish), used as a legacy id. */
export function hashToNumericId(input: string): number {
  let hash = 5381;
  for (let i = 0; i < input.length; i += 1) {
    hash = ((hash << 5) + hash + input.charCodeAt(i)) | 0;
  }
  // Force positive 31-bit integer.
  return Math.abs(hash);
}

function deriveBadge(ad: AdListItem): BadgeType {
  if (ad.is_urgent) return 'Urgent';
  if (ad.is_featured) return 'Verified';
  if (ad.condition === 'new') return 'New';
  return 'Hot Deal';
}

function deriveTag(ad: AdListItem): string {
  const raw = (ad.category_path || ad.category_name || '')
    .split('>')[0]
    ?.trim()
    .toLowerCase();
  if (raw && TAG_FROM_PATH[raw]) return TAG_FROM_PATH[raw];
  // Fall back to the category_name title-cased.
  return ad.category_name || 'Other';
}

function formatPrice(price: string | number | null | undefined): string {
  if (price === null || price === undefined || price === '') return 'PKR —';
  const num = typeof price === 'number' ? price : Number(price);
  if (!Number.isFinite(num)) return `PKR ${price}`;
  return new Intl.NumberFormat('en-PK', {
    style: 'currency',
    currency: 'PKR',
    maximumFractionDigits: 0,
  }).format(num);
}

function formatRelative(iso: string | undefined): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '';
  const diffMs = Date.now() - then;
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  const years = Math.round(months / 12);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}

function combineLocation(ad: AdListItem): string {
  return [ad.location, ad.city].filter(Boolean).join(', ') || '—';
}

export function adListItemToListing(ad: AdListItem): Listing {
  const img = ad.primary_image || ad.images?.[0] || FALLBACK_IMAGE;
  return {
    id: hashToNumericId(ad.id),
    uuid: ad.id,
    title: ad.title,
    price: formatPrice(ad.price),
    location: combineLocation(ad),
    time: formatRelative(ad.created_at),
    badge: deriveBadge(ad),
    tag: deriveTag(ad),
    img,
    color: '#F5F5F5',
    featured: Boolean(ad.is_featured),
    subcategory: ad.category_name,
    description: ad.description || ad.short_description,
    year: ad.year ?? undefined,
    mileage: ad.mileage ?? undefined,
  };
}
